import { useMemo } from 'react';
import { useAuthState } from '@/lib/auth/authStore';
import type { Role } from '@/lib/api/schemas';

/**
 * Capability -> minimum audience. Mirrors the backend `role:` middleware (admin = admin + super_admin,
 * super_admin always passes). The server is the authority; this only decides what to SHOW.
 * Screen agents may append capabilities here (one line each).
 */
export const CAPABILITIES = {
  'catalog.view': 'all',
  'catalog.manage': 'admin',
  'cost.view': 'admin', // cost_price / unit_cost / supplier data are never sent to cashiers
  'orders.create': 'all',
  'bills.view': 'all',
  'reports.view': 'admin',
  'users.manage': 'admin',
  'activity.view': 'admin',
  'settings.view': 'all',
  'settings.edit': 'admin',
  'settings.system': 'super_admin', // rooms / shifts / system lock
  'shifts.manage': 'admin',
  'admin.area': 'super_admin',
} as const satisfies Record<string, 'all' | 'admin' | 'super_admin'>;

export type Capability = keyof typeof CAPABILITIES;

export function roleSatisfies(
  role: Role | undefined | null,
  need: 'all' | 'admin' | 'super_admin',
): boolean {
  if (!role) return false;
  if (role === 'super_admin') return true;
  if (need === 'all') return true;
  if (need === 'admin') return role === 'admin';
  return false;
}

export function usePermissions() {
  const { user } = useAuthState();
  const role = user?.role ?? null;

  return useMemo(
    () => ({
      role,
      /** admin OR super_admin (matches backend `role:admin`). */
      isAdmin: role === 'admin' || role === 'super_admin',
      isSuperAdmin: role === 'super_admin',
      isCashier: role === 'cashier',
      /** exact role match, no hierarchy: hasRole('cashier', 'admin') */
      hasRole: (...roles: Role[]) => !!role && roles.includes(role),
      can: (capability: Capability) => roleSatisfies(role, CAPABILITIES[capability]),
    }),
    [role],
  );
}
